import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { Discount, DiscountWriteModel } from "../../models/Discount";
import MaterialTable from "@material-table/core";
import moment from "moment";
import { DiscountDescription } from "../../components/misc/DiscountDescription";
import { DiscountState } from "../../store/discounts/types";
import { isOk } from "../../result";
import * as DiscountApi from "../../api/discountApi";
import { isPeriodicDiscount } from "./utils";
import { thunkFetchDiscounts } from "../../store/discounts/actions";
import { Alert } from "@material-ui/lab";
import { Outlet, useNavigate, useOutletContext } from "react-router-dom";
import { theme } from "../stores/StoreList/components/styles";
import { useI18n } from "../../hooks/useI18n";

type DiscountEditContext = {
  createDiscount: Function;
  updateDiscount: Function;
};

export function useDiscountEditContext() {
  return useOutletContext<DiscountEditContext>();
}

interface AlertMessage {
  description: string;
  severity: "error" | "success";
}

interface DiscountsState {
  alertMessage?: AlertMessage;
}

export const Discounts = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useI18n();

  const discountState = useSelector<AppState, DiscountState>(s => s.discounts);
  const { discounts, discountTypes, isLoading } = discountState;
  const [state, setState] = useState<DiscountsState>({});

  useEffect(() => {
    if (!discounts.length) {
      dispatch(thunkFetchDiscounts());
    }
  }, []);

  const editDiscount = (discount: Discount) => {
    navigate(discount.id);
  };

  const addDiscount = () => {
    navigate("new");
  };

  const updateDiscount = (model: DiscountWriteModel) => {
    DiscountApi.update(model.id ?? "motha fka", model).then(result => {
      if (isOk(result)) {
        onSaveSuccess({
          description: `Coupon ${model.code} successfully updated`,
          severity: "success"
        });
      } else {
        onSaveError({
          description: `Unable to update coupon ${model.code}`,
          severity: "error"
        });
      }
    });
  };

  const createDiscount = (model: DiscountWriteModel) => {
    let discount = { ...model };
    if (isPeriodicDiscount(discount.type, discountTypes)) {
      discount.start = new Date();
      discount.end = new Date("3000-12-31");
    }

    DiscountApi.add(discount).then(result => {
      if (isOk(result)) {
        onSaveSuccess({
          description: `Coupon ${discount.code} successfully created`,
          severity: "success"
        });
      } else {
        onSaveError({
          description: `Unable to create coupon ${discount.code}. Make sure it does not exist already.`,
          severity: "error"
        });
      }
    });
  };

  const onSaveSuccess = (success: AlertMessage) => {
    setState({
      ...state,
      alertMessage: success
    });
    dispatch(thunkFetchDiscounts());
  };

  const onSaveError = (error: AlertMessage) =>
    setState({
      ...state,
      alertMessage: error
    });

  const removeAlertMessage = () => {
    setState({
      ...state,
      alertMessage: undefined
    });
  };
  return (
    <>
      {state.alertMessage && (
        <Alert
          severity={state.alertMessage.severity}
          onClose={() => removeAlertMessage()}
          style={{ marginBottom: 1 }}
        >
          {state.alertMessage.description}
        </Alert>
      )}
      <MaterialTable
        title={t("common.messages.coupons")}
        isLoading={isLoading}
        columns={[
          {
            title: t("common.type"),
            render: discount => {
              const discountType = discountTypes.find(
                dt => dt.id === discount.type
              );
              if (discountType !== undefined) {
                return discountType.label;
              }

              return t("common.system"); // TODO
            }
          },
          { title: t("common.name"), field: "name" },
          {
            title: t("common.description"),
            render: discount => <DiscountDescription discount={discount} />
          },
          {
            title: t("common.start"),
            field: "start",
            render: discount => moment(discount.start).format("YYYY-MM-DD")
          },
          {
            title: t("common.end"),
            field: "end",
            render: discount => moment(discount.end).format("YYYY-MM-DD")
          },
          {
            title: t("common.tableHeaders.tenantId"),
            field: "tenantId",
            hidden: true,
            searchable: true
          }
        ]}
        data={(discounts as Discount[]) || []}
        onRowClick={(e: any, discount: any) => {
          editDiscount(discount as Discount);
        }}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: theme.palette.primary.main, fontSize: "2rem" }
            },
            tooltip: t("common.addNewCoupon"),
            isFreeAction: true,
            onClick: addDiscount
          }
        ]}
        options={{
          actionsColumnIndex: -1,
          pageSize: 10
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />
      <Outlet
        context={{
          createDiscount: createDiscount,
          updateDiscount: updateDiscount
        }}
      />
    </>
  );
};
